import { useCallback, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArcherContainer, ArcherElement } from "@specmatic/react-archer";
import {
  EyeIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  ClockIcon,
  BoltIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { ActionCard } from "./WorkflowCards";
import { NODE_TYPE } from "../../services/Constants";
import { SectionTitle } from "../home-page/SectionHeading";

const GOAL_WORKFLOWS = [
  {
    id: "cart-abandonment",
    label: "Cart Abandonment Recovery",
    steps: [
      {
        id: "ca-trigger",
        type: NODE_TYPE.TRIGGER,
        title: "Cart Abandoned",
        description:
          "User adds items to cart but doesn't checkout within 1 hour",
        details: {
          triggerCondition: "Cart.status === 'abandoned' && timeSince > 1h",
          targetSegment: "Active Shoppers",
          frequency: "Real-time",
        },
      },
      {
        id: "ca-delay-1",
        type: NODE_TYPE.DELAY,
        delay: 1,
        sortIndex: 0,
        details: { duration: "1 day", unit: "days" },
      },
      {
        id: "ca-email-1",
        type: NODE_TYPE.EMAIL,
        title: "Reminder Email",
        emailSubject: "You left something in your cart!",
        sortIndex: 1,
        details: {
          subject: "You left something in your cart!",
          template: "cart_reminder_v1",
          recipients: "User.email",
        },
      },
      {
        id: "ca-delay-2",
        type: NODE_TYPE.DELAY,
        delay: 2,
        sortIndex: 2,
        details: { duration: "2 days", unit: "days" },
      },
      {
        id: "ca-email-2",
        type: NODE_TYPE.EMAIL,
        title: "Last Chance Email",
        emailSubject: "Your cart expires in 24 hours",
        sortIndex: 3,
        details: {
          subject: "Your cart expires in 24 hours",
          template: "cart_urgency_v1",
          recipients: "User.email",
        },
      },
    ],
  },
  {
    id: "new-user-onboarding",
    label: "New User Onboarding",
    steps: [
      {
        id: "nu-trigger",
        type: NODE_TYPE.TRIGGER,
        title: "New Account Created",
        description: "User completes registration and verifies email",
        details: {
          triggerCondition: "User.status === 'verified' && isFirstLogin",
          targetSegment: "New Users",
          frequency: "Real-time",
        },
      },
      {
        id: "nu-delay-1",
        type: NODE_TYPE.DELAY,
        delay: 0,
        sortIndex: 0,
        details: { duration: "Immediate", unit: "days" },
      },
      {
        id: "nu-email-1",
        type: NODE_TYPE.EMAIL,
        title: "Welcome Email",
        emailSubject: "Welcome aboard, here's how to get started",
        sortIndex: 1,
        details: {
          subject: "Welcome aboard, here's how to get started",
          template: "welcome_onboarding_v1",
          recipients: "User.email",
        },
      },
      {
        id: "nu-delay-2",
        type: NODE_TYPE.DELAY,
        delay: 3,
        sortIndex: 2,
        details: { duration: "3 days", unit: "days" },
      },
      {
        id: "nu-email-2",
        type: NODE_TYPE.EMAIL,
        title: "Quick-Start Guide",
        emailSubject: "3 things to try in your first week",
        sortIndex: 3,
        details: {
          subject: "3 things to try in your first week",
          template: "onboarding_tips_v1",
          recipients: "User.email",
        },
      },
      {
        id: "nu-delay-3",
        type: NODE_TYPE.DELAY,
        delay: 7,
        sortIndex: 4,
        details: { duration: "7 days", unit: "days" },
      },
      {
        id: "nu-email-3",
        type: NODE_TYPE.EMAIL,
        title: "Check-In Email",
        emailSubject: "How's it going? We'd love your feedback",
        sortIndex: 5,
        details: {
          subject: "How's it going? We'd love your feedback",
          template: "onboarding_checkin_v1",
          recipients: "User.email",
        },
      },
    ],
  },
  {
    id: "re-engagement",
    label: "Re-engagement Campaign",
    steps: [
      {
        id: "re-trigger",
        type: NODE_TYPE.TRIGGER,
        title: "User Gone Inactive",
        description: "No login or activity for 30+ days",
        details: {
          triggerCondition: "daysSinceLastActivity >= 30",
          targetSegment: "Lapsed Users",
          frequency: "Daily batch",
        },
      },
      {
        id: "re-delay-1",
        type: NODE_TYPE.DELAY,
        delay: 1,
        sortIndex: 0,
        details: { duration: "1 day", unit: "days" },
      },
      {
        id: "re-email-1",
        type: NODE_TYPE.EMAIL,
        title: "We Miss You Email",
        emailSubject: "It's been a while, see what's new",
        sortIndex: 1,
        details: {
          subject: "It's been a while, see what's new",
          template: "reengagement_v1",
          recipients: "User.email",
        },
      },
      {
        id: "re-delay-2",
        type: NODE_TYPE.DELAY,
        delay: 5,
        sortIndex: 2,
        details: { duration: "5 days", unit: "days" },
      },
      {
        id: "re-email-2",
        type: NODE_TYPE.EMAIL,
        title: "Exclusive Offer Email",
        emailSubject: "A special offer just for you, 20% off",
        sortIndex: 3,
        details: {
          subject: "A special offer just for you, 20% off",
          template: "reengagement_offer_v1",
          recipients: "User.email",
        },
      },
    ],
  },
  {
    id: "appointment-reminder",
    label: "Appointment Reminder",
    steps: [
      {
        id: "ar-trigger",
        type: NODE_TYPE.TRIGGER,
        title: "Appointment Booked",
        description: "Patient or client books an upcoming appointment",
        details: {
          triggerCondition: "Appointment.status === 'confirmed'",
          targetSegment: "Booked Clients",
          frequency: "Real-time",
        },
      },
      {
        id: "ar-delay-1",
        type: NODE_TYPE.DELAY,
        delay: 0,
        sortIndex: 0,
        details: { duration: "Immediate", unit: "days" },
      },
      {
        id: "ar-email-1",
        type: NODE_TYPE.EMAIL,
        title: "Booking Confirmation",
        emailSubject: "Your appointment is confirmed",
        sortIndex: 1,
        details: {
          subject: "Your appointment is confirmed",
          template: "booking_confirmation_v1",
          recipients: "Client.email",
        },
      },
      {
        id: "ar-delay-2",
        type: NODE_TYPE.DELAY,
        delay: 0,
        sortIndex: 2,
        details: { duration: "1 day before appointment", unit: "relative" },
      },
      {
        id: "ar-email-2",
        type: NODE_TYPE.EMAIL,
        title: "Reminder Email",
        emailSubject: "Reminder: your appointment is tomorrow",
        sortIndex: 3,
        details: {
          subject: "Reminder: your appointment is tomorrow",
          template: "appointment_reminder_v1",
          recipients: "Client.email",
        },
      },
      {
        id: "ar-delay-3",
        type: NODE_TYPE.DELAY,
        delay: 1,
        sortIndex: 4,
        details: { duration: "1 day after appointment", unit: "relative" },
      },
      {
        id: "ar-email-3",
        type: NODE_TYPE.EMAIL,
        title: "Follow-Up Email",
        emailSubject: "How was your visit? Book your next appointment",
        sortIndex: 5,
        details: {
          subject: "How was your visit? Book your next appointment",
          template: "post_appointment_v1",
          recipients: "Client.email",
        },
      },
    ],
  },
];

const PANEL_TYPE_CONFIG = {
  [NODE_TYPE.TRIGGER]: {
    icon: BoltIcon,
    label: "Goal",
    pillClasses: "border-blue-100 bg-blue-50 text-blue-700",
  },
  [NODE_TYPE.EMAIL]: {
    icon: EnvelopeIcon,
    label: "Email",
    pillClasses: "border-blue-100 bg-blue-50 text-blue-700",
  },
  [NODE_TYPE.DELAY]: {
    icon: ClockIcon,
    label: "Delay",
    pillClasses: "border-amber-100 bg-amber-50 text-amber-700",
  },
};

const SettingsField = ({ label, value, mono = false }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
      {label}
    </label>
    <div
      className={`text-sm text-gray-900 bg-gray-50 border border-gray-200 px-3 py-2.5 rounded-lg ${mono ? "font-mono text-xs" : ""}`}
    >
      {value}
    </div>
  </div>
);

const VerticalFlowDiagram = ({ onStepClick }) => {
  const [activeGoalId, setActiveGoalId] = useState(GOAL_WORKFLOWS[0].id);

  const activeGoal = GOAL_WORKFLOWS.find((g) => g.id === activeGoalId);
  const steps = activeGoal.steps;

  const [selectedStep, setSelectedStep] = useState(steps[0]);
  const [hoveredStep, setHoveredStep] = useState(null);

  const handleGoalChange = useCallback((goalId) => {
    setActiveGoalId(goalId);
    const goal = GOAL_WORKFLOWS.find((g) => g.id === goalId);
    setSelectedStep(goal.steps[0]);
    setHoveredStep(null);
  }, []);

  const handleStepClick = useCallback(
    (step) => {
      setSelectedStep(step);
      if (onStepClick) onStepClick(step);
    },
    [onStepClick],
  );

  const handleStepHover = useCallback((step) => {
    setHoveredStep(step);
  }, []);

  const handleStepLeave = useCallback(() => {
    setHoveredStep(null);
  }, []);

  const displayStep = hoveredStep || selectedStep;

  const nodeRelations = useMemo(() => {
    const relations = {};
    steps.forEach((step, index) => {
      if (index < steps.length - 1) {
        const nextStep = steps[index + 1];
        relations[step.id] = [
          {
            targetId: nextStep.id,
            sourceAnchor: "bottom",
            targetAnchor: "top",
            style: {
              strokeColor: "#d1d5db",
              strokeWidth: 1,
              lineStyle: "straight",
              endShape: {
                arrow: { arrowLength: 8, arrowThickness: 8 },
              },
            },
          },
        ];
      }
    });
    return relations;
  }, [steps]);

  const getRelationsForNode = useCallback(
    (nodeId) => nodeRelations[nodeId] || [],
    [nodeRelations],
  );

  const renderDetailsPanel = () => {
    if (!displayStep) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center h-full flex items-center justify-center">
          <p className="text-sm text-gray-400">
            Hover or click a step to preview its settings
          </p>
        </div>
      );
    }

    const config =
      PANEL_TYPE_CONFIG[displayStep.type] || PANEL_TYPE_CONFIG[NODE_TYPE.EMAIL];
    const Icon = config.icon;
    const isEmail = displayStep.type === NODE_TYPE.EMAIL;
    const isDelay = displayStep.type === NODE_TYPE.DELAY;

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-gray-50/60">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            Node Settings
            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-blue-600">{config.label}</span>
          </div>
          {isEmail && (
            <button className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-md transition-colors">
              <EyeIcon className="w-3.5 h-3.5" />
              Preview
            </button>
          )}
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4.5 h-4.5 text-gray-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {displayStep.title || config.label}
              </p>
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold mt-0.5 ${config.pillClasses}`}
              >
                <Icon className="h-3 w-3" />
                {config.label}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {displayStep.description && (
              <SettingsField
                label="Description"
                value={displayStep.description}
              />
            )}

            {isEmail && displayStep.emailSubject && (
              <SettingsField label="Subject" value={displayStep.emailSubject} />
            )}

            {isDelay && displayStep.delay != null && (
              <SettingsField
                label="Duration"
                value={`Wait ${displayStep.delay} day${displayStep.delay === 1 ? "" : "s"}`}
              />
            )}

            {displayStep.details && (
              <div className="space-y-3 pt-3 border-t border-gray-100">
                {Object.entries(displayStep.details).map(([key, value]) => (
                  <SettingsField
                    key={key}
                    label={key.replace(/([A-Z])/g, " $1").trim()}
                    value={value}
                    mono
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200">
            <button className="px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors">
              Cancel
            </button>
            <button className="px-4 py-1.5 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors">
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section
      id="platform"
      className="bg-[#fafafa] px-6 py-28 lg:px-10 lg:py-36"
      style={{ fontFamily: "var(--font-geist)" }}
    >
      <div className="mx-auto max-w-6xl">
        {/* Section heading */}
        <SectionTitle
          center
          eyebrow="Inside the platform"
          title="One goal in."
          em="Full workflow out."
          body="Set a goal and Fluvo builds the entire engagement workflow. Click any node to refine the message, timing or strategy — customise anything Fluvo AI creates."
        />

        {/* Platform preview */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <img
            src="/assets/fluvo-screenshot.png"
            alt="Fluvo workflow dashboard showing stakeholders, goals, and automated email campaigns"
            className="w-full rounded-2xl border border-neutral-200"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default VerticalFlowDiagram;
